import {
  HttpClient,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ConstantMan } from './constantMan';
import { NavigationError, Router, RoutesRecognized } from '@angular/router';
import { share } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
const appVersion = require('../../../package.json').version;
import * as uuid from 'uuid';

@Injectable()
export class ApiHelper {
  private accessToken: any;
  private serviceName: string;
  private busySource = new Subject<any>();
  private apiErrorSource = new Subject<any>();

  busyChange$ = this.busySource.asObservable();
  apiErrorChange$ = this.apiErrorSource.asObservable();
  isBrowser: boolean;

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  public setEnvironment(envArg: string) {
    switch (envArg) {
      case 'local':
        this.serviceName = ConstantMan.API.BASE.LOCAL;
        break;
      case 'local-like-production':
        this.serviceName = ConstantMan.API.BASE.LOCAL;
        break;
      case 'dev':
        this.serviceName = ConstantMan.API.BASE.DEV;
        break;
      case 'stage':
        this.serviceName = ConstantMan.API.BASE.STAGE;
        break;
      case 'prod':
        this.serviceName = ConstantMan.API.BASE.PROD;
        break;
      default:
        this.serviceName = ConstantMan.API.BASE.DEV;
    }
  }

  public getServiceName(): string {
    return this.serviceName;
  }

  public getDefaultHeader(): HttpHeaders {
    let headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    if (appVersion) {
      headers = headers.set('x-client-version', appVersion);
    }
    headers = headers.set('x-request-id', uuid.v4());
    let token = this.getAccessToken();
    /* Was getting back 'undefined' as a string so changed !token to check more thoroughly */
    if (!token || (typeof token === 'string' && token === 'undefined')) {
      return headers;
    }

    // unlike the deprecated Headers this set() doesn't modify the original object...so return
    headers = headers.set('Authorization', `Bearer ${token}`);

    return headers;
  }

  /**
   * Returns an observable of the specified resource type
   */
  // TODO lock headers to httpHeaders
  public apiCall<T>(
    url: string,
    method: string,
    params: any,
    data: any,
    headers: any,
    resource?: string,
    blob?: boolean,
    loadIndicator: boolean = true,
  ): Observable<T> {
    if (resource) {
      url = url + resource;
    }
    if (!data) {
      data = {};
    }

    let initObj = {};
    if (headers) {
      initObj['headers'] = headers as HttpHeaders;
    }
    if (params) {
      initObj['params'] = params;
    }
    if (blob) {
      initObj['responseType'] = 'blob';
    }

    let callHttpRequest: HttpRequest<T>;
    /* see the comments by the two actual methods */
    if (
      ConstantMan.API.GET_METHODS.some((methodName) => method === methodName)
    ) {
      callHttpRequest = this.nonpayloadHttpRequest<T>(method, url, initObj);
    } else {
      callHttpRequest = this.payloadHttpRequest<T>(method, url, data, initObj);
    }

    let apiCallObservable: Observable<T> = Observable.create((observer) => {
      let httpRequest = this.httpClient
        .request<T>(callHttpRequest)
        .pipe(share());
      if (loadIndicator) {
        this.setBusy(httpRequest);
      }
      httpRequest.subscribe(
        (responseData: HttpResponse<T>) => {
          /**
           * This check handles the CORS preflight OPTIONS request.  It won't be an instance of HtppResponse
           * since it has no body.  We want to ignore these as long as they come back with a 2xx status code.
           * Interceptor should handle the errors
           */
          if (responseData instanceof HttpResponse) {
            observer.next(responseData.body);
          } else {
            console.log('Thats the 204');
          }
        },
        (err) => {
          observer.error(err);
        },
      );
    });

    return apiCallObservable;
  }

  /**
   * For request methods without a data payload, I.E. 'DELETE' | 'GET' | 'HEAD' | 'JSONP' | 'OPTIONS'.
   * The HttpClient constructor definition for these call types doesn't have a data/body parameter.
   *
   * If the same four default parameters were to be passed to a GET method it would try to use the body parameters
   * as the init parameter(appending them as URL query parameters and then ignoring the actual init parameter)
   *
   * Takes three parameters by default
   */
  private nonpayloadHttpRequest<T>(
    method: string,
    url: string,
    params: any,
  ): HttpRequest<T> {
    return new HttpRequest<T>(method, url, params);
  }

  /**
   * For request methods that commonly have a data payload, I.E. 'POST' | 'PUT' | 'PATCH',
   * also the default call, to match the actual HttpClient constructor definitions.
   *
   * Takes four parameters by default
   */
  private payloadHttpRequest<T>(
    method: string,
    url: string,
    data: any,
    params: any,
  ): HttpRequest<T> {
    return new HttpRequest<T>(method, url, data, params);
  }

  public handleApiError(apiError) {
    console.log(apiError);
    if (apiError && apiError.error && apiError.error.disableDefaultHandling) {
      return;
    }
    if (apiError.hasOwnProperty('status')) {
      switch (apiError.status) {
        case 400:
          console.log('Bad Request', apiError);
          if (
            window.location.pathname.includes('manage') ||
            apiError.error.applicationError ||
            apiError.error.isAnchorApplicationError
          ) {
            if (!apiError.blockDefaultErrorHandling) {
              this.setApiError({
                title: apiError.error.title
                  ? apiError.error.title
                  : apiError.error.name,
                message: apiError.error.message
                  ? apiError.error.message
                  : apiError.error.description,
              });
            }
          }
          break;
        case 401:
          console.log('Unauthorized');
          this.setApiError({
            title: 'Access Forbidden',
            message: 'You do not currently have access to that resource.',
          });
          this.router.navigate(['/login']);
          break;
        case 403:
          console.log('Forbidden');
          if (apiError.url.includes('login')) {
            this.setApiError({
              title: 'Login Failed',
              message: 'The username / password combination was invalid.',
            });
            if (!window.location.pathname.includes('customer/booking')) {
              this.router.navigate(['/login']);
            }
          } else if (apiError.url.includes('reset')) {
            this.setApiError({
              title: 'Token Expired',
              message: 'You cannot reset your password at this time.',
            });
            this.router.navigate(['/login/forgot-password']);
          } else {
            this.setApiError({
              title: 'Access Forbidden',
              message: 'You do not currently have access to that resource.',
            });
            this.router.navigate(['/manage']);
          }
          break;
        case 404:
          console.log('Not Found');
          this.setApiError({
            title: '404',
            message: 'Page not found.',
          });
          this.router.navigate(['/login']);
          break;
        case 405:
          this.setApiError({
            title: 'Processing Error',
            message: apiError.statusText,
          });
          break;
        default:
          console.log('HANDLING ERROR', apiError);
          this.setApiError({
            title: apiError.status + ' ' + apiError.statusText,
            message: apiError._body,
          });
      }
    } else {
      console.log('unhandled API error');
    }
  }

  public setAccessToken(token: string) {
    this.accessToken = token;
    if (this.isBrowser) {
      window.localStorage.setItem('accessToken', token);
    }
  }

  public removeAccessToken() {
    this.accessToken = null;
    if (this.isBrowser) {
      window.localStorage.removeItem('accessToken');
    }
  }

  public getAccessToken() {
    if (this.isBrowser) {
      this.accessToken = window.localStorage.getItem('accessToken');
    }
    return this.accessToken;
  }

  public setBusy(busy: Observable<any>) {
    this.busySource.next(busy);
  }

  public setApiError(apiError) {
    console.log(apiError);
    this.apiErrorSource.next(apiError);
  }
}
